import type { HTMLAttributes, ReactNode } from 'react'

import { cn } from '@/lib/cn'

type CardProps = Omit<HTMLAttributes<HTMLElement>, 'title'> & {
  title?: ReactNode
  /** Мелкая подпись под заголовком. */
  subtitle?: ReactNode
  /** Кнопки или ссылки справа от заголовка. */
  actions?: ReactNode
  /** Без внутренних отступов: таблицы и графики во всю ширину. */
  flush?: boolean
}

export function Card({ title, subtitle, actions, flush, className, children, ...rest }: CardProps) {
  return (
    <section className={cn('min-w-0 rounded-xl border border-border bg-surface', className)} {...rest}>
      {title || actions ? (
        <header className="flex flex-wrap items-start gap-x-3 gap-y-2 px-4 pt-3.5">
          {/* Основа 6rem: не влезают рядом — действия переносятся под заголовок. */}
          <div className="min-w-0 flex-[1_1_6rem]">
            {title ? <h2 className="text-sm font-semibold text-fg">{title}</h2> : null}
            {subtitle ? <p className="mt-0.5 text-sm text-muted">{subtitle}</p> : null}
          </div>
          {actions ? <div className="flex max-w-full shrink-0 flex-wrap items-center gap-1 [&>a]:hit-area">{actions}</div> : null}
        </header>
      ) : null}
      <div className={cn(flush ? 'pt-3' : 'p-4', title || actions ? (flush ? '' : 'pt-3') : '')}>
        {children}
      </div>
    </section>
  )
}

/** Крупное число с подписью: «Вес 79,4 кг». */
export function Stat({
  label,
  value,
  unit,
  hint,
  className,
}: {
  label: ReactNode
  value: ReactNode
  unit?: ReactNode
  hint?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="text-xs font-medium uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-2xl font-semibold tabular-nums text-fg">{value}</span>
        {unit ? <span className="text-sm text-muted">{unit}</span> : null}
      </div>
      {hint ? <div className="mt-0.5 text-xs text-muted">{hint}</div> : null}
    </div>
  )
}
